import type { HTMLAttributes, MouseEvent, ReactNode } from "react";
import { forwardRef } from "react";

import { cx } from "class-variance-authority";

import { Tooltip } from "../Tooltip";

import { SidebarBackArrow } from "./icons/SidebarBackArrow";
import { SidebarCaret } from "./icons/SidebarCaret";
import { SidebarLockBadge } from "./icons/SidebarLockBadge";
import * as theme from "./SidebarMenuItem.css";
import { SidebarRow } from "./SidebarRow";

export type SidebarMenuItemType =
    | "noCaret"
    | "caretCollapsed"
    | "caretExpanded"
    | "iconOnly"
    | "back";

export interface SidebarMenuItemProps extends Omit<
    HTMLAttributes<HTMLElement>,
    "onClick"
> {
    label: string;
    /** Ignored by `back`, which draws its own chevron. */
    icon?: ReactNode;
    type?: SidebarMenuItemType;
    /** Renders an anchor when given, a button otherwise. */
    href?: string;
    onClick?: (event: MouseEvent<HTMLElement>) => void;
    /** The page currently open. Ignored on a group header, which has no page of its own. */
    active?: boolean;
    locked?: boolean;
    lockedTooltip?: string;
    className?: string;
}

// Forwards its ref so the row can be handed to a Radix `asChild` trigger.
// `forwardRef`, not a `ref` prop: legacy's React 18 drops `ref` props.
export const SidebarMenuItem = forwardRef<HTMLElement, SidebarMenuItemProps>(
    (
        {
            label,
            icon,
            type = "noCaret",
            href,
            onClick,
            active = false,
            locked = false,
            lockedTooltip,
            className,
            ...rest
        },
        ref
    ) => {
        const isIconOnly = type === "iconOnly";
        const isExpanded = type === "caretExpanded";
        const hasCaret = (type === "caretCollapsed" || isExpanded) && !locked;
        const isActive = active && !hasCaret;

        const collapsedLabel = isIconOnly ? label : undefined;
        const tooltip = locked ? lockedTooltip : collapsedLabel;

        return (
            // `disabled` swaps the tree out, remounting the row and killing its fade.
            <Tooltip
                title={tooltip}
                placement="right"
                noWrapper
                open={tooltip === undefined ? false : undefined}
            >
                <SidebarRow
                    ref={ref}
                    href={href}
                    locked={locked}
                    onClick={onClick}
                    data-slot="sidebar-menu-item"
                    className={cx(
                        theme.root,
                        isIconOnly && theme.iconOnly,
                        type === "back" && theme.back,
                        isActive && theme.active,
                        locked && theme.locked,
                        className
                    )}
                    aria-current={isActive ? "page" : undefined}
                    aria-label={collapsedLabel}
                    aria-expanded={hasCaret ? isExpanded : undefined}
                    {...rest}
                >
                    <span className={theme.icon} aria-hidden>
                        {type === "back" ? <SidebarBackArrow /> : icon}
                    </span>
                    <span
                        className={cx(
                            theme.tail,
                            isIconOnly && theme.collapsedTail
                        )}
                        aria-hidden={isIconOnly || undefined}
                    >
                        <span className={theme.label}>{label}</span>
                        {locked && <SidebarLockBadge />}
                        {hasCaret && <SidebarCaret expanded={isExpanded} />}
                    </span>
                    {isIconOnly && locked && (
                        <SidebarLockBadge className={theme.iconOnlyLock} />
                    )}
                </SidebarRow>
            </Tooltip>
        );
    }
);
